import React, { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { toast } from "react-toastify";
import accessURL from "../../../../../../../components/utils/access";
import { exportReportsToExcel } from "../../../../../../../components/utils/tableExport";
import {
  Reports,
  ReportsPagination,
} from "../../../../../../../salesRep/components/schedule/types/types";
import FilterSection from "../../../../../../../salesRep/components/reports/reports/FilterSection";
import Pagination from "../../../../../../../salesRep/components/reports/reports/Pagination";
import ReportCard from "./ReportCard";

interface Filters {
  startDate?: string;
  endDate?: string;
  dateOfVisit?: boolean;
  customer?: boolean;
  customerCategory?: boolean;
  followUpPlan?: boolean;
  customerName?: string;
}

const RepReport: React.FC<{ id: number }> = ({ id }) => {
  const [reports, setReports] = useState<Reports[]>([]);
  const [filters, setFilters] = useState<Filters>({});
  const [pagination, setPagination] = useState<ReportsPagination>({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    limit: 10,
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const fetchReports = async () => {
    setLoading(true);

    const params = {
      page: pagination.currentPage,
      limit: pagination.limit,
      startDate: filters.startDate,
      endDate: filters.endDate,
      customerName: filters.customerName,
      dateOfVisit: filters.dateOfVisit,
      customer: filters.customer,
      customerCategory: filters.customerCategory,
      followUpPlan: filters.followUpPlan,
    };

    setError(null);
    try {
      const response = await accessURL.get(`reports/singleReport?id=${id}`, {
        params,
      });
      if (response.status >= 400 || !response.data?.data?.reports) {
        throw new Error(response.data?.message || "Unable to load reports.");
      }
      setReports(response.data.data.reports);
      setPagination(response.data.data.pagination);
    } catch (error) {
      setError(error instanceof Error ? error.message : "Unable to load reports. Please try again.");
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [pagination.currentPage, filters, id]);

  const handlePageChange = (pageNumber: number) => {
    setPagination((prev) => ({ ...prev, currentPage: pageNumber }));
  };

  const handleFilterChange = (newFilters: Filters) => {
    setFilters(newFilters);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleExportReports = async () => {
    if (!id) return;

    try {
      setIsExporting(true);
      await exportReportsToExcel({
        client: accessURL,
        endpoint: `reports/singleReport?id=${id}`,
        filters,
        fileName: `manager-reports-${id}-${new Date().toISOString().split("T")[0]}.csv`,
        sheetName: "Reports",
      });
      toast.success("Reports exported successfully!");
    } catch (error) {
      console.error("Error exporting reports:", error);
      toast.error("Failed to export reports.");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div>
      <FilterSection onFilterChange={handleFilterChange} />
      <div className="flex justify-end mt-4 mb-2">
        <button
          onClick={handleExportReports}
          disabled={!!error || loading || isExporting || pagination.totalRecords === 0 || !id}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <Download className="h-4 w-4" />
          {isExporting ? "Exporting..." : "Export Reports"}
        </button>
      </div>
      {error && <p role="alert" className="my-4 text-red-600">{error}</p>}
      {loading ? (
        <div className="flex justify-center items-center my-10">
          <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <>
          {error ? null : reports.length === 0 ? (
            <div className="flex justify-center items-center my-10 text-gray-500">
              <p>No reports available at the moment. 📄</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {reports.map((report) => (
                <ReportCard key={report.id} report={report} />
              ))}
            </div>
          )}
          <Pagination
            pagination={pagination}
            handlePageChange={handlePageChange}
          />
        </>
      )}
    </div>
  );
};

export default RepReport;
